import React, { useState, useEffect } from 'react';
import { Thermometer, AlertCircle, Edit, Trash2, Power, Save } from 'lucide-react';
import TemperatureGauge from './TemperatureGauge';

/**
 * Tank Card Component
 * Displays monitoring data for a single tank
 * 
 * @param {Object} props Component properties
 * @param {Object} props.tank Tank data object
 * @param {Function} props.onEdit Function to call when edit button is clicked
 * @param {Function} props.onDelete Function to call when delete button is clicked
 * @param {Function} props.onToggleJacket Function to call when toggling jacket state
 * @param {Function} props.onSetTemperature Function to call when setting temperature
 */
const TankCard = ({ tank, onEdit, onDelete, onToggleJacket, onSetTemperature }) => {
  const [tempInput, setTempInput] = useState(tank.targetTemp || 55);
  
  useEffect(() => {
    setTempInput(tank.targetTemp || 55);
  }, [tank.targetTemp]);
  
  // Calculate if data is stale (more than 10 minutes old)
  const isDataStale = tank.lastUpdated ? 
    (new Date() - new Date(tank.lastUpdated)) > 10 * 60 * 1000 : 
    true;
  
  // Handle submit of new temperature
  const handleSubmitTemp = () => {
    onSetTemperature(tank.tankID, tempInput);
  };
  
  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmitTemp();
    }
  };
  
  return (
    <div className="col-lg-3 col-md-6 mb-4">
      <div className={`card h-100 ${isDataStale ? 'border-warning' : ''}`}>
        <div className="card-header py-2 d-flex justify-content-between align-items-center">
          <h6 className="card-title mb-0">{tank.tankName}</h6>
          <div className="btn-group btn-group-sm">
            <button
              onClick={() => onToggleJacket(tank.tankID, !tank.jacketOn)}
              className={`btn btn-sm ${
                tank.jacketOn ? 'btn-primary' : 'btn-outline-secondary'
              }`}
              title="Toggle Cooling Jacket"
            >
              <Power size={14} />
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary" 
              onClick={() => onEdit(tank)}
              title="Edit Tank"
            >
              <Edit size={14} />
            </button>
            <button 
              className="btn btn-sm btn-outline-danger" 
              onClick={() => onDelete(tank.tankID)}
              title="Remove Tank"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
        <div className="card-body py-2">
          {isDataStale && (
            <div className="alert alert-warning py-1 mb-2" style={{fontSize: '0.8rem'}}>
              <small className="d-flex align-items-center">
                <AlertCircle size={12} className="me-1" />
                Data may be stale
              </small>
            </div>
          )}
          
          <div className="d-flex align-items-center mb-2">
            <Thermometer 
              size={18} 
              className={tank.temperature !== null ? (
                Math.abs(tank.temperature - tank.targetTemp) <= 2 ? 'text-success' : 
                Math.abs(tank.temperature - tank.targetTemp) <= 5 ? 'text-warning' : 
                'text-danger'
              ) : 'text-secondary'} 
            />
            <div className="ms-2">{tank.temperature !== null ? `${tank.temperature}°F` : 'N/A'}</div>
            <div className="ms-auto">
              <small className="text-muted">Target: {tank.targetTemp}°F</small>
            </div>
          </div>
          
          {/* Temperature gauge visualization */}
          <TemperatureGauge 
            temperature={tank.temperature} 
            targetTemp={tank.targetTemp} 
          />
          
          <div className="input-group input-group-sm mb-2">
            <input
              type="number"
              className="form-control form-control-sm"
              value={tempInput}
              onChange={(e) => setTempInput(e.target.value)}
              onKeyPress={handleKeyPress}
              min="40"
              max="90"
              step="0.1"
            />
            <button 
              className="btn btn-outline-primary" 
              onClick={handleSubmitTemp}
              title="Set Temperature"
            >
              <Save size={14} />
            </button>
          </div>
          
          <div className="d-flex justify-content-between align-items-center">
            <span className={`badge ${tank.jacketOn ? 'bg-primary' : 'bg-secondary'}`}>
              CONTROL {tank.jacketOn ? 'ON' : 'OFF'}
            </span>
            {tank.lastUpdated && (
              <small className="text-muted" style={{fontSize: '0.7rem'}}>
                {new Date(tank.lastUpdated).toLocaleTimeString()}
              </small>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TankCard;